import { formatDateTime } from "@/lib/status";
import {
  FilePlus2, ShoppingCart, Send, Clock, PackageCheck, CheckCircle2,
  XCircle, BellRing, AlertTriangle, Circle, Pencil, Repeat,
} from "lucide-react";

const CH_LABEL = { whatsapp: "WhatsApp", mail: "E-posta", b2b: "B2B", app: "Uygulama" };

// Her durum için ikon + renk (zaman çizelgesi noktası)
const STATUS_VISUAL = {
  "Oluşturuldu": { Icon: FilePlus2, cls: "bg-slate-100 text-slate-600 ring-slate-200" },
  "Satınalmacıya İletildi": { Icon: ShoppingCart, cls: "bg-amber-100 text-amber-700 ring-amber-200" },
  "WhatsApp'a Aktarıldı": { Icon: Send, cls: "bg-green-100 text-green-700 ring-green-200" },
  "Mail ile Gönderildi": { Icon: Send, cls: "bg-sky-100 text-sky-700 ring-sky-200" },
  "Beklemede": { Icon: Clock, cls: "bg-amber-100 text-amber-700 ring-amber-200" },
  "Kısmi Sipariş": { Icon: Send, cls: "bg-cyan-100 text-cyan-700 ring-cyan-200" },
  "Sipariş Verildi": { Icon: Send, cls: "bg-blue-100 text-blue-700 ring-blue-200" },
  "Kontrol Bekliyor": { Icon: Clock, cls: "bg-violet-100 text-violet-700 ring-violet-200" },
  "Kısmi Geldi": { Icon: PackageCheck, cls: "bg-indigo-100 text-indigo-700 ring-indigo-200" },
  "Tam Geldi": { Icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700 ring-emerald-200" },
  "Kapatıldı": { Icon: CheckCircle2, cls: "bg-violet-100 text-violet-700 ring-violet-200" },
  "İptal": { Icon: XCircle, cls: "bg-rose-100 text-rose-700 ring-rose-200" },
};
const DEFAULT_VISUAL = { Icon: Circle, cls: "bg-slate-100 text-slate-600 ring-slate-200" };

function buildEvents(request) {
  const events = [];
  (request.status_history || []).forEach((h) => {
    const isEdit = (h.note || "").includes("düzenlendi");
    const v = isEdit
      ? { Icon: Pencil, cls: "bg-slate-100 text-slate-600 ring-slate-200" }
      : (STATUS_VISUAL[h.status] || DEFAULT_VISUAL);
    events.push({ at: h.at, by: h.by, title: h.status, detail: h.note || "", ...v });
  });
  (request.reminder_history || []).forEach((r) => {
    const parts = [r.supplier_name, CH_LABEL[r.channel] || r.channel];
    if (r.pending_count) parts.push(`${r.pending_count} bekleyen kalem`);
    let detail = parts.filter(Boolean).join(" • ");
    if (r.note) detail += ` — ${r.note}`;
    events.push({ at: r.at, by: r.by, title: "Tedarikçiye Hatırlatma", detail, Icon: BellRing, cls: "bg-amber-100 text-amber-700 ring-amber-200" });
  });
  (request.discrepancy_history || []).forEach((d) => {
    const parts = [d.recipient_label, d.supplier_name, CH_LABEL[d.channel] || d.channel];
    if (d.problem_count) parts.push(`${d.problem_count} kalem`);
    let detail = parts.filter(Boolean).join(" • ");
    if (d.note) detail += ` — ${d.note}`;
    events.push({ at: d.at, by: d.by, title: "Uyumsuzluk / İade Bildirimi", detail, Icon: AlertTriangle, cls: "bg-rose-100 text-rose-700 ring-rose-200" });
  });
  (request.order_history || []).forEach((o) => {
    if (o.type === "exchange") {
      const fromStr = `${o.from_code || ""} ${o.from_name || ""}`.trim();
      const toStr = `${o.to_code || ""} ${o.to_name || ""}`.trim();
      events.push({
        at: o.at, by: o.by,
        title: "Ürün Değişimi (Değişim Talebi)",
        detail: `${fromStr} → ${toStr}`,
        Icon: Repeat, cls: "bg-teal-100 text-teal-700 ring-teal-200",
      });
    }
  });
  events.sort((a, b) => new Date(a.at) - new Date(b.at));
  return events;
}

export default function RequestTimeline({ request }) {
  const events = buildEvents(request);
  return (
    <div data-testid="request-timeline" className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-4">Sipariş Geçmişi</div>
      {events.length === 0 ? (
        <div data-testid="timeline-empty" className="text-sm text-slate-400 py-6 text-center">Henüz hareket yok</div>
      ) : (
        <ol className="relative">
          {events.map((e, idx) => {
            const Icon = e.Icon || Circle;
            const last = idx === events.length - 1;
            return (
              <li key={idx} data-testid={`timeline-event-${idx}`} className={`relative flex gap-3.5 ${last ? "" : "pb-5"}`}>
                {!last && <span className="absolute left-[15px] top-9 bottom-0 w-px bg-slate-200" aria-hidden="true" />}
                <span className={`relative z-10 shrink-0 h-8 w-8 rounded-full flex items-center justify-center ring-1 ${e.cls}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 text-sm leading-tight">{e.title}</div>
                  {e.detail && <div className="text-xs text-slate-600 mt-0.5 break-words">{e.detail}</div>}
                  <div className="text-[11px] text-slate-400 mt-1">{formatDateTime(e.at)} • {e.by}</div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
